import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Award,
  ArrowDownRight,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  Crown,
  Download,
  Flame,
  Gauge,
  Layers,
  Loader2,
  Medal,
  Minus,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Trophy,
  User as UserIcon,
  Users,
} from 'lucide-react'
import { toast } from 'sonner'
import { api, extractApiError } from '../../lib/api'
import { formatNumber } from '../../lib/format'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Button } from '../../components/ui/Button'
import { Avatar } from '../../components/Avatar'
import { EmptyStateBanner } from '../../components/EmptyStateBanner'
import { neutraliseFormula } from '../../components/table/tableExport.js'
import {
  leaderboardResponse,
  myGamificationStats,
  performanceOverview,
  workspaceListResponse,
} from '../../lib/types'
import { useAuthStore } from '../../stores/authStore'

/** Friendly label + icon for each workRole tier — reused across the
 *  personal hero, per-role rollup, and leaderboard chips so the same
 *  tier is recognisable everywhere on the page. */
const ROLE_META = {
  manager: { label: 'Manager', Icon: Crown },
  team_leader: { label: 'Team Leader', Icon: ShieldCheck },
  employee: { label: 'Employee', Icon: UserIcon },
  unassigned: { label: 'No role set', Icon: UserIcon },
}

/** Total number of badges the platform supports. Used only for the
 *  "Badges earned X / 12" suffix on the personal stat card. The full
 *  per-badge catalogue (codes + labels + descriptions + accent colours)
 *  was removed when the trophy-case grid was retired; if it ever comes
 *  back, restore from git history or the gamification service which is
 *  the source of truth for `BadgeAward.badgeCode`. */
const BADGE_TOTAL = 12

const WINDOWS = [
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last quarter' },
  { days: 365, label: 'Last year' },
]

export function PerformancePage() {
  const user = useAuthStore((s) => s.user)
  // The merged page has two surfaces:
  //   1. "Your recognition" — personal badges + total points + ledger.
  //      Visible to every tenant user (role === 'user'), regardless of
  //      work-role tier. Company Admins skip this section because they
  //      don't earn points on their own work; they consume only the
  //      team analytics below.
  //   2. "Team performance" — KPIs, leaderboard, dept throughput, CSV
  //      export. Visible to anyone with the `pages.performance` op —
  //      Company Admin always, plus Managers / Team Leaders by tier
  //      (and any custom role with the op ticked). Employees never
  //      see this section, but the page still loads for them so the
  //      personal section above is reachable from the sidebar.
  //
  // Page mode summary:
  //   super_admin  → never lands here (no tenant)
  //   company_admin → team section only
  //   user + pages.performance op → both sections (Manager / Team Leader)
  //   user without op → personal section only (Employee default)
  const showPersonal = user?.role === 'user'
  const canSeeTeam = useAuthStore((s) =>
    user?.role === 'company_admin' || s.hasOperation('pages.performance'),
  )

  const [days, setDays] = useState(30)
  // Empty string = "All Workspaces" (no filter). Same UX pattern as the
  // Members page filter — Performance numbers can mean very different
  // things across multiple workspaces, especially the dept breakdown.
  const [workspaceId, setWorkspaceId] = useState('')

  const workspacesQuery = useQuery({
    queryKey: ['workspaces', 'mine'],
    queryFn: async () => {
      const res = await api.get('/workspaces/mine')
      return workspaceListResponse.parse(res.data)
    },
    enabled: canSeeTeam,
  })
  const workspaces = workspacesQuery.data?.data ?? []
  // Workspace de-emphasis (Phase 1): hide the Workspace filter for companies
  // running a single Workspace.
  const hasMultipleWorkspaces = workspaces.length > 1

  const perfQuery = useQuery({
    queryKey: ['performance', days, workspaceId],
    queryFn: async () => {
      const res = await api.get('/admin/performance', {
        params: {
          days,
          workspaceId: workspaceId || undefined,
        },
      })
      return performanceOverview.parse(res.data)
    },
    enabled: canSeeTeam,
  })

  // Personal stats — gamification ledger + earned badges. Skipped for
  // Company Admin / Super Admin because they don't earn points.
  const myStatsQuery = useQuery({
    queryKey: ['gamification', 'me'],
    queryFn: async () => {
      const res = await api.get('/gamification/me')
      return myGamificationStats.parse(res.data)
    },
    enabled: showPersonal,
  })
  // Tenant top scorer — small social cue at the top of the personal
  // section so the user sees who they're chasing.
  const leaderboardQuery = useQuery({
    queryKey: ['gamification', 'leaderboard'],
    queryFn: async () => {
      const res = await api.get('/gamification/leaderboard')
      return leaderboardResponse.parse(res.data)
    },
    enabled: showPersonal,
  })

  return (
    <div>
      <Breadcrumb current="Performance" />

      <div className="mb-6">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
          <Gauge size={20} className="text-[color:var(--color-accent-strong)]" />
          Performance
        </h1>
        <p className="text-sm text-[color:var(--color-ink-muted)] mt-1">
          {showPersonal && canSeeTeam
            ? 'Your badges plus team stats.'
            : showPersonal
              ? 'Earn badges for tasks done on time.'
              : 'See how the team is doing.'}
        </p>
      </div>

      {/* ─── Your recognition (personal) ───────────────────────────── */}
      {showPersonal ? (
        <PersonalSection
          statsQuery={myStatsQuery}
          leaderboardQuery={leaderboardQuery}
        />
      ) : null}

      {/* ─── Team performance (supervisory) ────────────────────────── */}
      {canSeeTeam ? (
        <>
          {showPersonal ? (
            <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0 mt-10 mb-4">
              Team Performance
            </h2>
          ) : null}
          <div className="mb-4 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-0.5">
              {WINDOWS.map((w) => (
                <button
                  key={w.days}
                  type="button"
                  onClick={() => setDays(w.days)}
                  className={
                    'px-3 py-1.5 rounded text-xs font-medium cursor-pointer ' +
                    (days === w.days
                      ? 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-strong)] shadow-sm'
                      : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)]')
                  }
                >
                  {w.label}
                </button>
              ))}
            </div>
            {hasMultipleWorkspaces ? (
              <select
                value={workspaceId}
                onChange={(e) => setWorkspaceId(e.target.value)}
                aria-label="Workspace filter"
                className="h-9 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] text-xs min-w-[12rem]"
              >
                <option value="">All Workspaces</option>
                {workspaces.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
            ) : null}
            <Button
              variant="primary"
              size="sm"
              onClick={() =>
                exportPointsCsv(perfQuery.data, days, workspaceId, workspaces)
              }
              disabled={!perfQuery.data || perfQuery.data.pointsByUser.length === 0}
              title={
                !perfQuery.data
                  ? 'Loading…'
                  : perfQuery.data.pointsByUser.length === 0
                    ? 'Nothing to export yet'
                    : 'Download Points by Employee as CSV'
              }
              className="ml-auto"
            >
              <Download size={14} />
              Export CSV
            </Button>
          </div>

          {perfQuery.isLoading ? (
            <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Crunching numbers…
            </p>
          ) : perfQuery.isError ? (
            <p className="text-sm text-[color:var(--color-priority-urgent)]">
              {extractApiError(perfQuery.error).message}
            </p>
          ) : perfQuery.data ? (
            <Body data={perfQuery.data} />
          ) : null}
        </>
      ) : null}
    </div>
  )
}

/** Personal recognition section — Total points + badge gallery +
 *  recent ledger. Migrated wholesale from the retired AchievementsPage
 *  so users keep the trophy-case + ledger UI they were used to, just
 *  one page over. */
function PersonalSection({ statsQuery, leaderboardQuery }) {
  const stats = statsQuery.data
  const earnedCount = (stats?.badges ?? []).length
  const topRow = leaderboardQuery.data?.data?.[0]

  return (
    <>
      <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0 mb-3">
        Your Recognition
      </h2>
      {/* Stat row — same four-tile layout as the old Achievements page. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <PersonalStatCard
          label="Total Points"
          value={stats?.total ?? 0}
          accent="#6366f1"
          icon={Sparkles}
          tone="pink"
        />
        <PersonalStatCard
          label="Badges Earned"
          value={earnedCount}
          accent="#10b981"
          icon={Trophy}
          suffix={` / ${BADGE_TOTAL}`}
          tone="gold"
        />
        <PersonalStatCard
          label="Recent Awards"
          value={stats?.recent?.length ?? 0}
          accent="#f59e0b"
          icon={Award}
          tone="gold"
        />
        <PersonalStatCard
          label="Company Top Scorer"
          value={topRow ? `${topRow.points} pts` : '—'}
          accent="#ec4899"
          icon={Flame}
          subtitle={topRow?.fullName ?? null}
          small
        />
      </div>

      {/* Trophy case badge grid + Recent points ledger were removed
       *  on user request. The four-tile stat row above already
       *  summarises the same data at a high level (badges earned
       *  out of 12, recent-awards count). Restore from git history
       *  if the full per-badge gallery + ledger table are needed
       *  again. */}
    </>
  )
}

/** Personal stat tile — uses the project's canonical card heading class
 *  (`font-display text-lg ... font-semibold tracking-tight m-0`) for
 *  the label and the `font-display text-3xl` body for the value, so
 *  every tile across the app (Dashboard KpiTile, UserHome KpiTile, and
 *  this Performance personal-stats tile) reads at the same scale.
 *  Standing rule: card headings are never rendered as uppercase
 *  eyebrows — that style was retired when the font/family pass
 *  unified the headings across all four panels. */
const PERSONAL_STAT_TONES = {
  gold: 'bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A] dark:bg-white/[0.06] dark:text-amber-300 dark:border-white/10',
  sky: 'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10',
  emerald: 'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10',
  amber: 'bg-[#FFEDD5] text-[#EA580C] border border-[#FED7AA] dark:bg-white/[0.06] dark:text-orange-300 dark:border-white/10',
  rose: 'bg-[#FFE4E6] text-[#E11D48] border border-[#FECDD3] dark:bg-white/[0.06] dark:text-rose-300 dark:border-white/10',
  violet: 'bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10',
  pink: 'bg-[#FCE7F3] text-[#DB2777] border border-[#FBCFE8] dark:bg-white/[0.06] dark:text-pink-300 dark:border-white/10',
}

function PersonalStatCard({ label, value, suffix, subtitle, icon: Icon, small, tone = 'sky' }) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0">
            {label}
          </h3>
          <p
            className={`mt-2 font-display text-[color:var(--color-ink-strong)] tabular-nums ${
              small ? 'text-xl' : 'text-3xl'
            }`}
          >
            {value}
            {suffix ? (
              <span className="text-sm font-normal text-[color:var(--color-ink-muted)] ml-1">
                {suffix}
              </span>
            ) : null}
          </p>
          {subtitle ? (
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)] truncate">
              {subtitle}
            </p>
          ) : null}
        </div>
        {/* Icon container — distinct hue per `tone` so the row reads
            with rhythm rather than as a flat grey strip. */}
        <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${PERSONAL_STAT_TONES[tone]}`}>
          <Icon size={20} />
        </div>
      </div>
    </div>
  )
}

// BadgeCard was removed when the trophy-case grid + Recent Points
// ledger were retired from the Performance page on user request. The
// four stat tiles above (Total points / Badges earned / Recent awards
// / Tenant top scorer) summarise the same data at a glance, which is
// enough recognition signal for this surface.

function Body({ data }) {
  const prev = data.previous
  const hasDept = data.throughputByDepartment.length > 0
  return (
    <>
      {/* Headline KPI strip — four metrics that tell the whole story
       *  at a glance. Dropped Rework / Decisions / Rejected from the
       *  earlier layout: those only matter when the approval chain is
       *  in active use, and they crowded the page with noise. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Kpi
          label="Tasks Completed"
          value={data.throughput}
          icon={CheckCircle2}
          tone="emerald"
          hint={`${data.totalApproved} delivered`}
          delta={deltaPercent(data.throughput, prev?.throughput)}
          deltaDirection="higher_is_better"
        />
        <Kpi
          label="On-Time Rate"
          value={`${data.onTimeRate}%`}
          icon={TrendingUp}
          tone={
            data.onTimeRate >= 80
              ? 'emerald'
              : data.onTimeRate >= 50
                ? 'amber'
                : 'rose'
          }
          hint={`${data.onTimeApproved} of ${data.totalApproved} on time`}
          delta={deltaPercent(data.onTimeRate, prev?.onTimeRate)}
          deltaDirection="higher_is_better"
        />
        <Kpi
          label="Cycle Time"
          value={
            data.avgCycleTimeHours != null
              ? `${data.avgCycleTimeHours}h`
              : '—'
          }
          icon={Clock}
          tone="sky"
          hint="Created → completed"
          delta={deltaPercent(data.avgCycleTimeHours, prev?.avgCycleTimeHours)}
          deltaDirection="lower_is_better"
        />
        {/* The hint formerly read "Across {N hours} logged" — the
         *  hours-logged context was retired 2026-05-27 with the rest
         *  of the Time Tracking module. The headline points value
         *  alone is the actionable signal; the previous hint was
         *  decorative. */}
        <Kpi
          label="Points Awarded"
          value={data.pointsAwarded}
          icon={Award}
          tone="violet"
          hint="From task completions and badges"
        />
      </div>

      {/* Two-column band: Points by Role + Throughput by Department
       *  (when present). When no department data exists we collapse
       *  the band to a single full-width Points-by-Role card so the
       *  page doesn't show a half-empty layout. */}
      {data.pointsByRole ? (
        <div
          className={
            'grid gap-4 mb-6 ' +
            (hasDept ? 'grid-cols-1 lg:grid-cols-[1.4fr_1fr]' : 'grid-cols-1')
          }
        >
          <PointsByRoleCard pointsByRole={data.pointsByRole} />
          {hasDept ? (
            <ThroughputByDepartmentCard rows={data.throughputByDepartment} />
          ) : null}
        </div>
      ) : null}

      <Leaderboard rows={data.pointsByUser} />
    </>
  )
}

/** Polished per-employee leaderboard. Rows show the bigger numbers
 *  the user actually cares about (total + a mini bar that gives
 *  immediate spatial sense of "how big is this score relative to the
 *  top"), plus inline coloured chips for the delivery split. Top-3
 *  ranks get a medal icon. */
function Leaderboard({ rows }) {
  const topPoints = rows[0]?.points ?? 0
  return (
    <div className="rounded-2xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-6 py-4 border-b border-[color:var(--color-rule)]">
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 rounded-xl bg-gradient-to-br from-[color:var(--color-accent-strong)] to-[color:var(--color-accent)] text-white flex items-center justify-center shadow-sm">
            <Trophy size={16} />
          </span>
          <div>
            <h2 className="font-display text-2xl text-[color:var(--color-ink-strong)] m-0">
              Leaderboard
            </h2>
            <p className="text-sm text-[color:var(--color-ink-muted)] m-0">
              Points awarded on task completion in this window.
            </p>
          </div>
        </div>
        <span className="text-xs text-[color:var(--color-ink-muted)] tabular-nums px-2 py-0.5 rounded-full border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]">
          {rows.length} {rows.length === 1 ? 'person' : 'people'}
        </span>
      </div>
      {rows.length === 0 ? (
        <div className="px-6 py-14 text-center">
          <span className="inline-flex h-14 w-14 rounded-2xl bg-[#FCE7F3] text-[#DB2777] border border-[#FBCFE8] dark:bg-white/[0.06] dark:text-pink-300 dark:border-white/10 items-center justify-center mb-3">
            <Sparkles size={22} />
          </span>
          <p className="text-sm font-semibold text-[color:var(--color-ink-strong)] m-0">
            No points awarded yet
          </p>
          <p className="mt-1.5 text-xs text-[color:var(--color-ink-muted)] max-w-md mx-auto">
            Mark a task as <strong>Done</strong> to seed the leaderboard.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-[color:var(--color-rule)]">
          {rows.map((row, i) => (
            <LeaderboardRow
              key={row.userId}
              row={row}
              rank={i + 1}
              maxPoints={topPoints || 1}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function LeaderboardRow({ row, rank, maxPoints }) {
  const isTop3 = rank <= 3
  const medalTone =
    rank === 1
      ? 'from-amber-400 to-yellow-500 text-white'
      : rank === 2
        ? 'from-slate-300 to-slate-400 text-white'
        : 'from-orange-300 to-orange-500 text-white'
  // Zero-point rows render an empty rank bar so a row with no
  // awards reads cleanly. Non-zero values keep the 2% min-width
  // floor for visibility.
  const pct =
    row.points > 0 ? Math.max(2, Math.round((row.points / maxPoints) * 100)) : 0
  const owner = row.ownerPoints ?? 0
  return (
    <li className="px-6 py-3.5 hover:bg-[color:var(--color-card-hover)] grid grid-cols-[44px_minmax(0,2.5fr)_minmax(0,1.5fr)_minmax(0,1fr)_140px] items-center gap-3">
      <span
        className={
          'inline-flex items-center justify-center h-9 w-9 rounded-xl text-sm font-display tabular-nums shrink-0 ' +
          (isTop3
            ? `bg-gradient-to-br ${medalTone} shadow-sm`
            : 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]')
        }
        title={isTop3 ? `#${rank} this window` : undefined}
      >
        {isTop3 ? <Medal size={16} /> : `#${rank}`}
      </span>
      <div className="flex items-center gap-3 min-w-0">
        <Avatar
          name={row.fullName}
          avatarUrl={row.avatarUrl ?? null}
          size="md"
        />
        <div className="min-w-0 flex flex-col gap-1">
          <p className="text-sm font-semibold text-[color:var(--color-ink-strong)] truncate m-0">
            {row.fullName}
          </p>
          <div className="flex items-center gap-1.5 flex-wrap">
            <RoleChip workRole={row.workRole} workRoleName={row.workRoleName} />
            {row.departmentName ? (
              <span className="text-xs text-[color:var(--color-ink-muted)] truncate">
                · {row.departmentName}
              </span>
            ) : null}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap min-w-0">
        {owner > 0 ? (
          <BreakdownChip tone="emerald" label="Own Tasks" value={owner} />
        ) : null}
        {owner === 0 ? (
          <span className="text-xs text-[color:var(--color-ink-muted)] italic">
            no breakdown
          </span>
        ) : null}
      </div>
      <div className="min-w-0">
        <div className="h-1.5 rounded-full bg-[color:var(--color-rule)] overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[color:var(--color-accent-strong)] to-[color:var(--color-accent)]"
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="mt-1 text-xs text-[color:var(--color-ink-muted)] tabular-nums">
          {row.awards} {row.awards === 1 ? 'award' : 'awards'}
        </p>
      </div>
      <p className="font-display text-xl text-[color:var(--color-ink-strong)] tabular-nums text-right m-0">
        {formatNumber(row.points)}
        <span className="text-xs text-[color:var(--color-ink-muted)] font-sans ml-1">
          pts
        </span>
      </p>
    </li>
  )
}

function BreakdownChip({ tone, label, value }) {
  const TONES = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10',
    sky: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10',
    amber: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-white/[0.06] dark:text-amber-300 dark:border-white/10',
  }
  return (
    <span
      className={
        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium border ' +
        TONES[tone]
      }
      title={`${label}: ${value} pts`}
    >
      {label}
      <span className="tabular-nums font-semibold">{value}</span>
    </span>
  )
}

/** Throughput by Department — only renders when the data exists. Cleaner
 *  visualisation than the previous empty-state-prone card: max 6 rows,
 *  proportional bars relative to the top department. */
function ThroughputByDepartmentCard({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.throughput))
  return (
    <div className="rounded-2xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
      <div className="flex items-center gap-2 mb-4">
        <span className="h-8 w-8 rounded-lg bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10 flex items-center justify-center">
          <Users size={14} />
        </span>
        <h2 className="font-display text-2xl text-[color:var(--color-ink-strong)] m-0">
          By Department
        </h2>
      </div>
      <div className="space-y-3">
        {rows.slice(0, 6).map((d) => {
          const pct = (d.throughput / max) * 100
          return (
            <div key={d.departmentId}>
              <div className="flex items-baseline justify-between mb-1">
                <p className="text-sm text-[color:var(--color-ink-strong)] truncate m-0">
                  {d.departmentName}
                </p>
                <p className="text-xs text-[color:var(--color-ink-muted)] tabular-nums m-0">
                  {d.throughput}
                </p>
              </div>
              <div className="h-2 rounded-full bg-[color:var(--color-rule)] overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[color:var(--color-accent-strong)] to-[color:var(--color-accent)]"
                  style={{ width: `${Math.max(pct, 2)}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** Per-role rollup card shown in the team body. Surfaces "who earned
 *  what" split by the actual assigned Work Role (operations-driven), so
 *  Company Admins see how points distribute across the roles THEY created
 *  (e.g. "Senior SOP Specialist", "Lead") — not three fixed tiers. */
function PointsByRoleCard({ pointsByRole }) {
  const rows = Array.isArray(pointsByRole) ? pointsByRole : []
  const anyData = rows.some((r) => (r.points ?? 0) > 0)
  return (
    <div className="rounded-2xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-lg bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10 flex items-center justify-center">
            <Layers size={14} />
          </span>
          <h2 className="font-display text-2xl text-[color:var(--color-ink-strong)] m-0">
            Points by Role
          </h2>
        </div>
        {anyData ? (
          <div className="hidden sm:flex items-center gap-3 text-xs text-[color:var(--color-ink-muted)]">
            <span className="inline-flex items-center gap-1">
              <span className="h-2 w-2 rounded-sm bg-[color:var(--color-success)]" />
              Own Tasks
            </span>
          </div>
        ) : null}
      </div>
      {!anyData ? (
        <EmptyStateBanner
          icon={TrendingUp}
          title="No points earned in this window"
          body="Mark a task as Done to start seeding scores. Points update across this dashboard in real time."
          tone="violet"
          compact
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((row) => {
            const total = Math.max(1, row.points)
            const ownerPct = Math.max(0, (row.ownerPoints / total) * 100)
            return (
              <div
                key={row.id ?? row.name}
                className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-4"
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="inline-flex items-center gap-1.5 text-base font-semibold text-[color:var(--color-ink-strong)] min-w-0">
                    <Layers size={16} className="shrink-0 text-[color:var(--color-accent-strong)]" />
                    <span className="truncate" title={row.name}>{row.name}</span>
                  </span>
                  <span className="text-xs text-[color:var(--color-ink-muted)]">
                    {row.users} {row.users === 1 ? 'person' : 'people'}
                  </span>
                </div>
                <p className="font-display text-2xl text-[color:var(--color-ink-strong)] tabular-nums leading-none m-0">
                  {formatNumber(row.points)}
                  <span className="text-xs text-[color:var(--color-ink-muted)] font-sans ml-1">
                    pts
                  </span>
                </p>
                <div className="h-1.5 rounded-full bg-[color:var(--color-card)] overflow-hidden flex mt-3">
                  {ownerPct > 0 ? (
                    <div
                      className="h-full bg-[color:var(--color-success)]"
                      style={{ width: `${ownerPct}%` }}
                      title={`Own Tasks: ${row.ownerPoints} pts`}
                    />
                  ) : null}
                </div>
                {/* Per-role points breakdown — full English so a
                 *  first-time viewer doesn't have to decode it. */}
                <p className="text-xs text-[color:var(--color-ink-muted)] mt-2 tabular-nums m-0">
                  {row.ownerPoints > 0
                    ? `Own Tasks ${row.ownerPoints}`
                    : ''}
                </p>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/** Compact role chip used inline in the leaderboard so admins can
 *  scan tiers at a glance without an extra column header lookup. The
 *  label uses the project's standard `text-xs` (12px) and the icon
 *  was bumped from 9 to 12 to stay readable at that size. */
function RoleChip({ workRole, workRoleName }) {
  const meta = ROLE_META[workRole ?? 'unassigned'] ?? ROLE_META.unassigned
  const Icon = meta.Icon
  // Prefer the ACTUAL assigned Work Role name (operations-driven); the tier
  // meta is only a fallback for its icon + when no custom role is assigned.
  const label = workRoleName ?? meta.label
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)]">
      <Icon size={12} />
      {label}
    </span>
  )
}

function Kpi({
  label,
  value,
  icon: Icon,
  tone,
  hint,
  delta,
  deltaDirection = 'higher_is_better',
}) {
  const TILE = {
    emerald:
      'bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border border-[color:var(--color-success-border)]',
    amber:
      'bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]',
    rose: 'bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger)] border border-[color:var(--color-danger-border)]',
    sky: 'bg-[color:var(--color-info-bg)] text-[color:var(--color-info)] border border-[color:var(--color-info-border)]',
    violet: 'bg-violet-50 text-violet-700 border border-violet-200 dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10',
  }
  return (
    <div className="rounded-2xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 transition-shadow hover:shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] m-0">
            {label}
          </h3>
          <p className="mt-2 font-display text-3xl text-[color:var(--color-ink-strong)] tabular-nums leading-none m-0">
            {typeof value === 'number' ? formatNumber(value) : value}
          </p>
          {hint ? (
            <p className="mt-2 text-[12px] text-[color:var(--color-ink-muted)] m-0">
              {hint}
            </p>
          ) : null}
          {delta !== null && delta !== undefined ? (
            <DeltaBadge delta={delta} direction={deltaDirection} />
          ) : null}
        </div>
        <div
          className={
            'h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ' +
            TILE[tone]
          }
        >
          <Icon size={20} />
        </div>
      </div>
    </div>
  )
}

/**
 * Compute the percentage change from `previous` to `current`. Returns
 * `null` when comparison isn't meaningful — null inputs, or zero
 * baseline (∞% jump from 0 → 1 is more confusing than helpful).
 */
function deltaPercent(current, previous) {
  if (current == null || previous == null) return null
  if (previous === 0) return null
  return Math.round(((current - previous) / previous) * 100)
}

/** Tiny pill that shows "↑ 12% vs prior 30 days" with red/green colour
 *  based on whether the change is favourable for this metric. Cycle time
 *  and rework rate are "lower is better"; throughput / on-time are
 *  "higher is better". */
function DeltaBadge({ delta, direction }) {
  if (delta === 0) {
    return (
      <p className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-medium text-[color:var(--color-ink-muted)]">
        <Minus size={11} />
        No change compared to prior window
      </p>
    )
  }
  const isUp = delta > 0
  const isFavourable =
    (direction === 'higher_is_better' && isUp) ||
    (direction === 'lower_is_better' && !isUp)
  const cls = isFavourable
    ? 'text-[color:var(--color-success)]'
    : 'text-[color:var(--color-priority-urgent)]'
  const Arrow = isUp ? ArrowUpRight : ArrowDownRight
  return (
    <p className={`mt-1.5 inline-flex items-center gap-1 text-[12px] font-medium ${cls}`}>
      <Arrow size={11} />
      {Math.abs(delta)}% compared to prior window
    </p>
  )
}

/**
 * Build a CSV from the Points by Employee table and trigger a download.
 * No backend round-trip — the data we already have is enough; encoding
 * it client-side keeps the export instantaneous.
 *
 * Filename encodes the window length and (when set) the workspace name
 * so admins can drop multiple exports into one folder without colliding.
 */
function exportPointsCsv(data, days, workspaceId, workspaces) {
  if (!data || data.pointsByUser.length === 0) {
    toast.info('Nothing to export yet — no points awarded in this window.')
    return
  }
  const wsName =
    workspaces.find((w) => w.id === workspaceId)?.name ?? null
  const headers = [
    'Rank',
    'Full Name',
    'Work Role',
    'Department',
    'Points',
    'Awards',
    'Own Tasks Points',
  ]
  const rows = data.pointsByUser.map((row, i) => [
    i + 1,
    row.fullName,
    ROLE_META[row.workRole ?? 'unassigned']?.label ?? '—',
    row.departmentName ?? '',
    row.points,
    row.awards,
    row.ownerPoints ?? 0,
  ])
  // RFC 4180-ish encoder — wrap fields containing commas / quotes /
  // newlines in quotes and double-up internal quotes.
  const escape = (v) => {
    // Names / departments are user-typed — neutralise formula injection.
    const s = neutraliseFormula(String(v ?? ''))
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
    return s
  }
  const csv = [headers, ...rows]
    .map((r) => r.map(escape).join(','))
    .join('\n')
  // BOM so Excel recognises UTF-8 (e.g. names with accented characters).
  const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  const stamp = new Date().toISOString().slice(0, 10)
  const wsSlug = wsName
    ? wsName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    : 'all-workspaces'
  a.href = url
  a.download = `taskmagix-performance-${days}d-${wsSlug}-${stamp}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
  toast.success('Exported Points by Employee as CSV.')
}
